package com.dignicate.web.theme

import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.dignicate.web.domain.Language
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.awaitCancellation
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event

@Composable
fun AppRoot(content: @Composable (locale: Language, isDark: Boolean) -> Unit) {
    var settings by remember { mutableStateOf(loadSettings()) }

    // System ダークの現在値を state 化（変化で再構成）
    var systemDark by remember { mutableStateOf(detectSystemDark()) }
    LaunchedEffect(Unit) {
        val mq = window.matchMedia("(prefers-color-scheme: dark)")
        val listener: (Event) -> Unit = { systemDark = mq.matches }
        mq.addEventListener("change", listener)
        try { awaitCancellation() } finally { mq.removeEventListener("change", listener) }
    }

    val effectiveLocale: Language = when (settings.language) {
        Language.System -> Language.fromCode(detectSystemLang())
        Language.Ja     -> Language.Ja
        Language.En     -> Language.En
        is Language.Other -> settings.language
    }

    val isDark = when (settings.theme) {
        ThemeMode.System -> systemDark
        ThemeMode.Light  -> false
        ThemeMode.Dark   -> true
    }

    // <html> に言語とテーマ属性を反映（SEO/アクセシビリティ）
    LaunchedEffect(effectiveLocale, isDark, settings.fontScale, settings.lineHeightScale) {
        document.documentElement?.setAttribute("lang", effectiveLocale.code)
        document.documentElement?.setAttribute("data-theme", if (isDark) "dark" else "light")
        // Also set root font-size and line-height based on settings to ensure language+scale update together
        try {
            val el = document.documentElement as? HTMLElement
            el?.style?.setProperty("font-size", "${"$"}{(settings.fontScale * 100).toInt()}%")
            el?.style?.setProperty("line-height", settings.lineHeightScale.toString())
        } catch (_: Throwable) {}
    }

    @Suppress("UNUSED_VALUE")
    CompositionLocalProvider(
        LocalSettings provides settings,
        LocalIsDark provides isDark,
        LocalLocale.provides(effectiveLocale),
        LocalUpdateSettings provides { new ->
            settings = new
            saveSettings(new)
            // call JS hook to sync favicon immediately when user changes theme in-app
            try {
                val win = window.asDynamic()
                val fn = win.setSiteTheme
                if (fn != undefined && fn != null) {
                    val mode = when (new.theme) {
                        ThemeMode.Dark -> "dark"
                        ThemeMode.Light -> "light"
                        else -> if (detectSystemDark()) "dark" else "light"
                    }
                    fn(mode)
                }
            } catch (_: Throwable) {
                // ignore if JS bridge not ready
            }
        }
    ) {
        content(effectiveLocale, isDark)
    }
}
